import { useState } from "react"
import { SparklesIcon, FilmIcon, SwapIcon, UndoIcon } from "./icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export function SceneActions({
  isEnhanced,
  isAnimated,
  isEnhancing,
  isAnimating,
  enhanceFailed,
  animateFailed,
  voiceoverCompleted,
  hasAnyAsset,
  disabled,
  horizontal,
  animateCapReached,
  onEnhance,
  onAnimate,
  onSwapImage,
  onRevert,
}: {
  isEnhanced: boolean
  isAnimated: boolean
  isEnhancing: boolean
  isAnimating: boolean
  enhanceFailed: boolean
  animateFailed: boolean
  voiceoverCompleted: boolean
  hasAnyAsset: boolean
  disabled?: boolean
  horizontal?: boolean
  animateCapReached?: boolean
  onEnhance: () => void
  onAnimate: () => void
  onSwapImage: () => void
  onRevert: () => void
}) {
  const processing = isEnhancing || isAnimating
  const [confirmOpen, setConfirmOpen] = useState(false)

  const handleRevert = () => {
    if (processing) {
      setConfirmOpen(true)
    } else {
      onRevert()
    }
  }

  return (
    <div className={`flex shrink-0 gap-1.5 ${horizontal ? "flex-row flex-wrap" : "flex-col"}`}>
      <ActionBtn onClick={onSwapImage} disabled={disabled} label="Change" icon={<SwapIcon />} />
      <ActionBtn
        onClick={onEnhance}
        disabled={disabled || processing}
        label={isEnhanced ? "Enhanced" : enhanceFailed ? "Retry" : "Enhance"}
        icon={<SparklesIcon />}
        variant={enhanceFailed ? "error" : isEnhanced ? "success" : "default"}
      />
      <ActionBtn
        onClick={onAnimate}
        disabled={disabled || processing || !voiceoverCompleted || (!!animateCapReached && !isAnimating)}
        label={isAnimated ? "Animated" : animateFailed ? "Retry" : "Animate"}
        icon={<FilmIcon />}
        variant={animateFailed ? "error" : isAnimated ? "success" : "default"}
        title={
          animateCapReached && !isAnimating && !isAnimated
            ? "3 clips already animating — wait for one to finish"
            : undefined
        }
      />
      {hasAnyAsset && (
        <ActionBtn
          onClick={handleRevert}
          disabled={disabled}
          label="Revert"
          icon={<UndoIcon />}
          variant="muted"
        />
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel operation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the current enhancement or animation and revert to the original image.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep going</AlertDialogCancel>
            <AlertDialogAction onClick={onRevert}>Revert</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function ActionBtn({
  onClick,
  disabled,
  label,
  icon,
  variant = "default",
  title,
}: {
  onClick: () => void
  disabled?: boolean
  label: string
  icon: React.ReactNode
  variant?: "default" | "success" | "error" | "muted"
  title?: string
}) {
  const colours = {
    default: "bg-white text-charcoal hover:bg-stone/10",
    success: "bg-red/5 text-red hover:bg-red/10",
    error: "bg-white text-red hover:bg-red/5",
    muted: "bg-white text-warm-grey hover:bg-stone/10 hover:text-red",
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title ?? label}
      className={`flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium shadow-sm ring-1 ring-stone/30 transition-all disabled:pointer-events-none disabled:opacity-40 ${colours[variant]}`}
    >
      {icon}
      <span className="leading-none">{label}</span>
    </button>
  )
}
